// Tiny fetch wrapper that:
//   • Prefixes calls with the API base URL.
//   • Adds the bearer token from AuthProvider when present.
//   • Auto-refreshes once on 401, then retries the original call.
//   • Always includes credentials so the refresh cookie travels with us.
//
// IMPORTANT: never import this from a non-React module without first
// calling configureApi() with a token getter (AuthProvider does it on
// mount). Otherwise authenticated calls will be silently anonymous.

const BASE = process.env.REACT_APP_API_BASE || '';

let _getToken = () => null;
let _setToken = () => {};
let _onAuthFail = () => {};

export function configureApi({ getToken, setToken, onAuthFail }) {
  if (getToken) _getToken = getToken;
  if (setToken) _setToken = setToken;
  if (onAuthFail) _onAuthFail = onAuthFail;
}

async function rawFetch(path, opts = {}) {
  const headers = new Headers(opts.headers || {});
  const token = _getToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (opts.body && !(opts.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(`${BASE}${path}`, {
    ...opts,
    headers,
    credentials: 'include',
  });
  return res;
}

async function tryRefresh() {
  const res = await fetch(`${BASE}/api/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) return null;
  const data = await res.json();
  _setToken(data.access_token, data.user);
  return data.access_token;
}

export async function api(path, opts = {}) {
  let res = await rawFetch(path, opts);
  if (res.status === 401 && !opts._retried) {
    const newToken = await tryRefresh();
    if (newToken) {
      return api(path, { ...opts, _retried: true });
    }
    _onAuthFail();
  }
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    let detailObj = null;
    try {
      const data = await res.clone().json();
      if (data?.detail) {
        if (typeof data.detail === 'string') {
          detail = data.detail;
        } else if (data.detail && typeof data.detail === 'object') {
          // Structured errors from the worker now look like
          // { detail: { code: 'image_too_large_for_db', message: '…' } }.
          // Prefer the message when present (human-readable), fall
          // back to JSON serialisation for unexpected shapes so we
          // never lose information.
          detailObj = data.detail;
          detail = typeof data.detail.message === 'string'
            ? data.detail.message
            : JSON.stringify(data.detail);
        }
      }
    } catch (_) { /* not json */ }
    const err = new Error(detail);
    err.status = res.status;
    if (detailObj) err.detail = detailObj;
    // Global hook: when an AI route returns 429 with code `ai_quota_exceeded`,
    // emit a custom event so a top-level <AiCreditsModalHost> can open the
    // credits modal automatically — single insertion point covers every
    // current and future AI consumer (vectorize/halftone/neon/dxf-gen/
    // assistant/image-enhance/…).
    if (res.status === 429 && detailObj && detailObj.code === 'ai_quota_exceeded') {
      try {
        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
          window.dispatchEvent(new CustomEvent('makercut:ai-quota-exceeded', {
            detail: { path, code: detailObj.code, message: detailObj.message || detail },
          }));
        }
      } catch (_) { /* ignore */ }
    }
    throw err;
  }
  if (res.status === 204) return null;
  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json') ? res.json() : res.text();
}

export const apiBase = BASE;
